import api from '../../api/apiClient';

/**
 * HR endpoints that had no client yet: the attendance register, payroll, salary release
 * and employment-status changes (`HRController`).
 */

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

const toArray = <T>(body: any): T[] => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

/**
 * One employee's payroll row for one month.
 *
 * These are the keys `GET /api/hr/attendance/{year}/{month}` actually returns. The previous
 * declaration here claimed `present` / `absent` / `leaves`, which the API has never sent —
 * so the register's three columns were permanently empty while every real figure (hours,
 * rate, gross, deductions, net) was discarded.
 */
export interface PayrollRow {
  id: number;
  employee_id: number;
  employee_code: string | null;
  employee_name: string | null;
  department: string | null;
  designation: string | null;
  year: number;
  month: number;
  total_days: number;
  working_days: number;
  standard_hours: number;
  working_hours: number;
  overtime_hours: number;
  negative_hours: number;
  null_hours: number;
  paid_leave_days: number;
  unpaid_leave_days: number;
  payable_hours: number;
  monthly_salary: number;
  per_hour_rate: number;
  gross_salary: number;
  incentive: number;
  advance: number;
  professional_tax: number;
  tds: number;
  net_payable: number;
  verified_by_hr: boolean;
  [key: string]: unknown;
}

/** Kept as an alias so existing imports of the register's row type keep working. */
export type AttendanceRow = PayrollRow;

export interface SalaryRelease {
  id?: number;
  is_salary_release_requested: boolean;
  /** Unreleased → Unpaid → Requested → Paid. */
  status: string;
  year: number;
  month: number;
  total_amount: string | null;
  employee_count: number;
  unverified_count: number;
  attendance_sheet: string | null;
  payment_proof: string | null;
}

export interface AttendanceEdit {
  total_days?: number;
  working_days?: number;
  working_hours?: number;
  overtime_hours?: number;
  negative_hours?: number;
  null_hours?: number;
  incentive?: number | string;
  verified_by_hr?: boolean;
}

/* ── Attendance register / payroll ───────────────────────────────────────── */

/** `GET /api/hr/attendance/{year}/{month}` — month is 1-12. */
export async function getPayroll(year: number, month: number): Promise<PayrollRow[]> {
  return toArray<PayrollRow>(await unwrap(api.get(`/api/hr/attendance/${year}/${month}`)));
}

/** Kept for callers that only want the register. */
export const getAttendance = getPayroll;

/**
 * `POST /api/hr/attendance/{year}/{month}/generate` — opens the month, creating one row per
 * employee on the books. Safe to re-run; pass `recalculate` to re-derive rows that exist.
 */
export async function generatePayrollMonth(
  year: number,
  month: number,
  recalculate = false
): Promise<{ created: number; updated: number; skipped: number; rows: PayrollRow[] }> {
  const body = await unwrap<any>(api.post(`/api/hr/attendance/${year}/${month}/generate`, { recalculate }));
  return body?.data ?? { created: 0, updated: 0, skipped: 0, rows: [] };
}

/** `PUT /api/hr/attendance/{employeeId}/{attendanceId}` — correct one row. */
export async function updateAttendance(
  employeeId: number | string,
  attendanceId: number | string,
  payload: AttendanceEdit
): Promise<PayrollRow> {
  const body = await unwrap<any>(api.put(`/api/hr/attendance/${employeeId}/${attendanceId}`, payload));
  return body?.data as PayrollRow;
}

/** `POST /api/hr/advance-salary` — pays an advance that the month then deducts. */
export async function payAdvanceSalary(payload: {
  employee_id: number;
  amount: number | string;
  payment_date?: string;
  payment_method?: string;
  particulars?: string;
}): Promise<any> {
  return unwrap(api.post('/api/hr/advance-salary', payload));
}

/* ── Salary release ──────────────────────────────────────────────────────── */

/** `GET /api/hr/release-salaries/{year}/{month}` — where this month stands. */
export async function getSalaryRelease(year: number, month: number): Promise<SalaryRelease> {
  const body = await unwrap<any>(api.get(`/api/hr/release-salaries/${year}/${month}`));
  return body?.data as SalaryRelease;
}

/** `POST /api/hr/release-salaries` — sends the month to Accounts and writes its payslips. */
export async function releaseSalaries(payload: {
  year: number;
  month: number;
  attendance_sheet?: number;
}): Promise<SalaryRelease> {
  const body = await unwrap<any>(api.post('/api/hr/release-salaries', payload));
  return body?.data as SalaryRelease;
}

/* ── Payslips ────────────────────────────────────────────────────────────── */

export interface Payslip {
  id: number;
  year: number;
  month: number;
  total_days: number;
  working_days: number;
  standard_hours: number;
  working_hours: number;
  overtime_hours: number;
  negative_hours: number;
  null_hours: number;
  paid_leave_days: number;
  unpaid_leave_days: number;
  payable_hours: number;
  monthly_salary: number;
  per_hour_rate: number;
  gross_salary: number;
  incentive: number;
  advance_deducted: number;
  professional_tax: number;
  tds: number;
  net_payable: number;
  status: string;
  generated_at: string | null;
}

/** `GET /api/hr/employee/{employeeId}/payslips` — newest month first. */
export async function getEmployeePayslips(employeeId: number | string): Promise<Payslip[]> {
  return toArray<Payslip>(await unwrap(api.get(`/api/hr/employee/${employeeId}/payslips`)));
}

/* ── Employee admin actions ──────────────────────────────────────────────── */

export async function updateEmploymentStatus(
  employeeId: number | string,
  employmentStatus: string
): Promise<any> {
  return unwrap(
    api.put(`/api/hr/employee/${employeeId}/employmentStatus`, {
      employment_status: employmentStatus,
    })
  );
}

export async function updateEmployee(
  employeeId: number | string,
  payload: Record<string, unknown>
): Promise<any> {
  return unwrap(api.put(`/api/hr/employee/${employeeId}`, payload));
}

/** Fetches one named document belonging to an employee. */
export async function getEmployeeDocument(
  employeeId: number | string,
  documentName: string
): Promise<any> {
  return unwrap(api.get(`/api/hr/employee/${employeeId}/${documentName}`));
}
